// /src/utils/calibration.js
const EPS = 1e-6;
const clamp = (x, a, b) => Math.min(b, Math.max(a, x));

function _quantile(sorted, q) {
  if (!sorted.length) return NaN;
  const idx = (sorted.length - 1) * q;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  const t = idx - lo;
  return sorted[lo] * (1 - t) + sorted[hi] * t;
}

export function computePercentiles(arr) {
  const v = arr.filter(x => Number.isFinite(x)).sort((a,b)=>a-b);
  if (!v.length) return {min:0,p10:0,p50:0,p90:0,max:0};
  const min = v[0], max = v[v.length-1];
  let p10 = _quantile(v, 0.10);
  let p50 = _quantile(v, 0.50);
  let p90 = _quantile(v, 0.90);
  // enforce monotonicity
  p10 = Math.min(Math.max(p10, min), p50);
  p90 = Math.max(Math.min(p90, max), p50);
  return { min, p10, p50, p90, max };
}

export function buildCalAnchors(pct) {
  // Defaults: band 35..80, inner anchors 40/55/75
  const outMin = 35, outMax = 80;
  let {min,p10,p50,p90,max} = pct;
  // Defensive widening for degenerate spans
  if (Math.abs(p50 - p10) < EPS) p10 = p50 - EPS;
  if (Math.abs(p90 - p50) < EPS) p90 = p50 + EPS;
  if (Math.abs(p10 - min) < EPS)  min = p10 - EPS;
  if (Math.abs(max - p90) < EPS)  max = p90 + EPS;
  return {
    outMin, outMax,
    a10: 40, a50: 55, a90: 75,
    pct: {min,p10,p50,p90,max}
  };
}

export function calibrateScore(raw, A) {
  const { outMin, outMax, a10, a50, a90, pct } = A;
  const { min, p10, p50, p90, max } = pct;
  if (!Number.isFinite(raw)) return (a50); // safe default

  if (raw <= min) return outMin;
  if (raw >= max) return outMax;

  if (raw <= p10) {
    const t = (raw - min) / Math.max(p10 - min, EPS);
    return outMin + t * (a10 - outMin);
  } else if (raw <= p50) {
    const t = (raw - p10) / Math.max(p50 - p10, EPS);
    return a10 + t * (a50 - a10);
  } else if (raw <= p90) {
    const t = (raw - p50) / Math.max(p90 - p50, EPS);
    return a50 + t * (a90 - a50);
  } else {
    const t = (raw - p90) / Math.max(max - p90, EPS);
    return a90 + t * (outMax - a90);
  }
}

export function normalizeScore(x, space, bounds) {
  if (!Number.isFinite(x)) return 0;
  if (space === 'cal') {
    const lo = bounds.calMin !== undefined ? bounds.calMin : 35;
    const hi = bounds.calMax !== undefined ? bounds.calMax : 80;
    return clamp((x - lo) / Math.max(hi - lo, EPS), 0, 1);
  }
  // 'raw' or 'auto'
  const {min, max} = bounds;
  return clamp((x - min) / Math.max(max - min, EPS), 0, 1);
}

export function calibrateUseCases(useCases) {
  const vs = useCases.map(u => Number(u.value_score));
  const es = useCases.map(u => Number(u.implementation_score));
  const vPct = computePercentiles(vs);
  const ePct = computePercentiles(es);
  const vA = buildCalAnchors(vPct);
  const eA = buildCalAnchors(ePct);

  const calMap = {};
  const calibratedUseCases = useCases.map(u => {
    const vCal = calibrateScore(Number(u.value_score), vA);
    const eCal = calibrateScore(Number(u.implementation_score), eA);
    // Total_cal mirrors 70/30 strategic by default; list can use other rankers
    const tCal = 0.70 * vCal + 0.30 * eCal;
    
    const calibrated = {
      ...u,
      value_score_cal: vCal,
      implementation_score_cal: eCal,
      total_score_cal: tCal
    };
    
    calMap[u.use_case_id] = { value: vCal, eoi: eCal, total: tCal };
    
    return calibrated;
  });

  return {
    useCases: calibratedUseCases,
    calMap,
    bounds: {
      value: { min: vPct.min, max: vPct.max, calMin: 35, calMax: 80 },
      eoi:   { min: ePct.min, max: ePct.max, calMin: 35, calMax: 80 }
    }
  };
}